/** Yahoo quotiert LSE-notierte Aktien in Pence (`currency: "GBp"`/"GBX", 1 GBP = 100 GBp,
 * live gegen British American Tobacco/BATS.L bestaetigt) - betrifft NUR die native
 * Kurs-Anzeige auf der Einzelaktien-Seite. Bewusst NICHT in lib/data/fx-rates.ts
 * (der bereits bestehende, separate Sub-Unit-Fix fuer die EUR-Depotberechnung) oder in
 * lib/data/providers/yahoo-provider.ts selbst geaendert: die EUR-Pipeline
 * (app/dashboard/page.tsx, getExchangeRate()) verlaesst sich bewusst auf die ROHEN
 * (unskalierten) Kurs-/Dividendenwerte + einen entsprechend angepassten Wechselkurs -
 * eine Aenderung an der Datenquelle wuerde dort ein bereits verifiziertes Ergebnis
 * gefaehrden. Diese Funktion beschraenkt sich daher bewusst auf einzelne
 * Anzeige-Stellen, die nachweislich NICHT Teil der EUR-Pipeline sind (siehe
 * backlog.md -> "Bekannte Bugs" fuer den vollen Scope inkl. noch offener Stellen).
 *
 * Name/Rueckgabewert historisch auf "Pence" gemuenzt (GBp/GBX), deckt aber bewusst
 * dasselbe Sub-Unit-Muster bei weiteren Boersen mit ab (proaktiv ergaenzt, siehe
 * lib/data/fx-rates.ts -> SUBUNIT_CURRENCIES fuer die volle Begruendung): "ZAc"
 * (Rand-Cents, Johannesburg) und "ILA" (Agorot, Tel Aviv), beide ebenfalls 1/100 der
 * Hauptwaehrung. Oeffentlicher Funktionsname bewusst nicht umbenannt (mehrere
 * Aufrufstellen site-weit) - der Docstring hier ist die verbindliche Quelle fuer den
 * tatsaechlichen Umfang. */
export function isPenceCurrency(currency: string | undefined): boolean {
  return currency === "GBp" || currency === "GBX" || currency === "ZAc" || currency === "ILA";
}

/** Nur fuer die native Anzeige, siehe isPenceCurrency() oben. */
export function descalePence(value: number, currency: string | undefined): number {
  return isPenceCurrency(currency) ? value / 100 : value;
}

/** Gleiches Prinzip wie descalePence(), fuer eine komplette Kurshistorie (Runde 8
 * Punkt 6-Ausweitung auf weitere Anzeige-Stellen) - descalePence() je Preisfeld,
 * unveraendert bei Nicht-Pence-Waehrungen (dann exakt dasselbe Array-Objekt, kein
 * unnoetiges Neu-Allozieren). */
export function descalePencePriceHistory<T extends { close: number; open?: number; high?: number; low?: number }>(
  points: T[],
  currency: string | undefined,
): T[] {
  if (!isPenceCurrency(currency)) return points;
  return points.map((p) => ({
    ...p,
    close: descalePence(p.close, currency),
    open: p.open !== undefined ? descalePence(p.open, currency) : undefined,
    high: p.high !== undefined ? descalePence(p.high, currency) : undefined,
    low: p.low !== undefined ? descalePence(p.low, currency) : undefined,
  }));
}

/** Gleiches Prinzip wie descalePencePriceHistory(), fuer eine Jahres-Werte-Serie
 * (z.B. Dividende je Aktie) statt einer Kurshistorie. */
export function descalePenceYearSeries<T extends { value: number }>(
  series: T[],
  currency: string | undefined,
): T[] {
  if (!isPenceCurrency(currency)) return series;
  return series.map((v) => ({ ...v, value: descalePence(v.value, currency) }));
}

export function formatPercent(value: number | undefined, digits = 1): string {
  if (value === undefined || Number.isNaN(value)) return "–";
  return `${(value * 100).toFixed(digits)} %`;
}

export function formatRatio(value: number | undefined, digits = 2): string {
  if (value === undefined || Number.isNaN(value)) return "–";
  return value.toFixed(digits);
}

export function formatCompactCurrency(
  value: number | undefined,
  currency = "USD",
): string {
  if (value === undefined || Number.isNaN(value)) return "–";
  return new Intl.NumberFormat("de-DE", {
    style: "currency",
    currency,
    notation: "compact",
    maximumFractionDigits: 1,
  }).format(value);
}

/** Exakter Betrag (nicht kompakt) - fuer einzelne Depot-Positionswerte, wo Rundung auf "1,2 Mio." falsch waere. */
export function formatCurrency(
  value: number | undefined,
  currency = "USD",
  digits = 2,
): string {
  if (value === undefined || Number.isNaN(value)) return "–";
  return new Intl.NumberFormat("de-DE", {
    style: "currency",
    currency,
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(value);
}

/** Fuer waehrungslose, aber grosse Stueckzahlen (z.B. ausstehende Aktien). */
export function formatCompactNumber(value: number | undefined): string {
  if (value === undefined || Number.isNaN(value)) return "–";
  return new Intl.NumberFormat("de-DE", {
    notation: "compact",
    maximumFractionDigits: 1,
  }).format(value);
}

/**
 * "Zuletzt aktualisiert"-Zeitstempel (backlog.md -> Aktienfinder-Benchmark, "Sichtbarer
 * 'Zuletzt aktualisiert'-Zeitstempel an Kennzahlen/Charts") - `fetchedAtMs` kommt aus
 * `getCacheFetchedAt()` (lib/data/cache.ts), dem ECHTEN Ursprungs-Zeitpunkt des
 * zugrunde liegenden Cache-Eintrags, kein erfundenes "gerade eben". undefined für einen
 * noch nie befüllten Cache-Key (z.B. ganz frischer Kaltstart) statt eines falschen Werts.
 */
export function formatDataFreshness(fetchedAtMs: number | undefined, now: Date = new Date()): string | undefined {
  if (fetchedAtMs === undefined) return undefined;
  // Math.floor (nicht round): "vor 1 Minute" soll erst nach einer VOLLEN Minute
  // erscheinen, nicht schon ab 30 Sekunden aufgerundet.
  const minutesAgo = Math.floor((now.getTime() - fetchedAtMs) / 60_000);
  if (minutesAgo < 1) return "Stand: gerade eben aktualisiert";
  if (minutesAgo < 60) return `Stand: vor ${minutesAgo} Minute${minutesAgo === 1 ? "" : "n"} aktualisiert`;
  const hoursAgo = Math.floor(minutesAgo / 60);
  if (hoursAgo < 24) return `Stand: vor ${hoursAgo} Stunde${hoursAgo === 1 ? "" : "n"} aktualisiert`;
  return `Stand: ${new Date(fetchedAtMs).toLocaleDateString("de-DE")} aktualisiert`;
}
